import { Form, FormIdField } from '@chargeafter/forms';
import { FC, useEffect } from 'react';
import { object, string } from 'yup';
import { Box } from '@mui/system';
import { PASSWORD_REGEX } from '../validations';
import { CircularProgress, List, ListItem, styled } from '@mui/material';
import { Typography, VerticalSpace } from '@chargeafter/ui';
import { TrackerButton } from '../components/TrackerButtonWrapper';
import { ModalHeader } from './ModalHeader';
import { useResetCurrentModal } from '../services/modalManager';
import logger from '../services/loggerManager';
import { changePassword, ChangePassword } from '../services/userManager';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import { FEEDBACK_TYPE } from '../services/feedbackManager';
import {
  CHANGE_PASSWORD_MODAL_TEXTS,
  NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS,
} from '../constants';

const validationSchema = object().shape({
  currentPassword: string().required('Current Password is a required field'),
  newPassword: string()
    .required('New Password is a required field')
    .matches(PASSWORD_REGEX, 'Password is too weak')
    .test(
      'CurrentPasswordsEqual',
      `New password must be different than the current password`,
      (value, context) => value !== context.parent.currentPassword,
    ),
  confirmedNewPassword: string()
    .required('Confirmed Password is a required field')
    .test(
      'ConfirmedPasswordsEqual',
      `Passwords don't match`,
      (value, context) => value === context.parent.newPassword,
    ),
});

const FormFieldsContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(3),
}));
const StyledFormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledFormLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  fontWeight: 700,
}));

interface ChangePasswordModalProps {
  updateClose: (func: () => void) => void;
  close: () => void;
}
const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  close,
  updateClose,
}) => {
  const resetCurrentModal = useResetCurrentModal();
  const [feedback] = useFeedbackSnackbar();

  useEffect(() => {
    updateClose(() => () => {
      resetCurrentModal();
      logger.log(`Closed change password modal`, {
        filename: 'ChangePasswordModal.tsx',
        function: 'closeModal',
      });
    });
  }, []);

  const handleSubmit = async (values: Record<string, any>) => {
    const body = values as ChangePassword;
    const errors = await changePassword(body);
    if (errors) return errors;

    feedback({ type: FEEDBACK_TYPE.PASSWORD_CHANGE_SUCCESS });
    return close();
  };

  return (
    <>
      <ModalHeader title={CHANGE_PASSWORD_MODAL_TEXTS.TITLE} />
      <Form
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        render={({
          submitting,
          submitErrors,
          touched,
          errors,
          dirtyFieldsSinceLastSubmit,
          hasValidationErrors,
        }) => {
          const invalidNewPassword =
            (!!touched?.['newPassword'] && !!errors?.['newPassword']) ||
            (!dirtyFieldsSinceLastSubmit['newPassword'] &&
              submitErrors?.['newPassword']);

          return (
            <>
              <Box display="flex">
                <FormFieldsContainer>
                  <StyledFormSection>
                    <StyledFormLabel variant="subtitle1">
                      {CHANGE_PASSWORD_MODAL_TEXTS.CURRENT_PASSWORD}
                    </StyledFormLabel>
                    <FormIdField
                      label={CHANGE_PASSWORD_MODAL_TEXTS.CURRENT_PASSWORD}
                      name="currentPassword"
                      maskVariant="default"
                    />
                  </StyledFormSection>

                  <StyledFormSection>
                    <StyledFormLabel variant="subtitle1">
                      {CHANGE_PASSWORD_MODAL_TEXTS.NEW_PASSWORD}
                    </StyledFormLabel>
                    <FormIdField
                      label={CHANGE_PASSWORD_MODAL_TEXTS.NEW_PASSWORD}
                      name="newPassword"
                      maskVariant="default"
                    />
                    <VerticalSpace units={1} />
                    <FormIdField
                      label={CHANGE_PASSWORD_MODAL_TEXTS.RE_ENTER_NEW_PASSWORD}
                      name="confirmedNewPassword"
                      maskVariant="default"
                    />
                  </StyledFormSection>
                </FormFieldsContainer>

                <PasswordRequirements invalidNewPassword={invalidNewPassword} />
              </Box>

              <TrackerButton
                fullWidth
                buttonName="save-new-password"
                disabled={submitting || hasValidationErrors}
                type="submit"
              >
                {submitting ? (
                  <CircularProgress color="inherit" size={35} thickness={5} />
                ) : (
                  CHANGE_PASSWORD_MODAL_TEXTS.SUBMIT_BUTTON_TEXT
                )}
              </TrackerButton>
            </>
          );
        }}
      />
    </>
  );
};

export default ChangePasswordModal;

const StyledContainer = styled(Box)<{ error: boolean }>(({ theme, error }) => ({
  margin: theme.spacing(3),
  padding: theme.spacing(2, 4),
  backgroundColor: error
    ? theme.palette.error.light
    : theme.palette.secondary.light,
}));

const StyledList = styled(List)({
  listStyleType: 'disc',
  listStylePosition: 'inside',
});

const StyledListItem = styled(ListItem)({
  display: 'list-item',
});

interface PasswordRequirementsProps {
  invalidNewPassword: boolean;
}

const PasswordRequirements: FC<PasswordRequirementsProps> = ({
  invalidNewPassword,
}) => {
  return (
    <StyledContainer error={invalidNewPassword}>
      <Typography variant="h6">
        {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.TITLE}
      </Typography>
      <StyledList dense>
        <StyledListItem>
          {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.ITEM1}
        </StyledListItem>
        <StyledListItem>
          {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.ITEM2}
        </StyledListItem>
        <StyledListItem>
          {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.ITEM3}
        </StyledListItem>
        <StyledListItem>
          {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.ITEM4}
        </StyledListItem>
        <StyledListItem>
          {NEW_PASSWORD_REQUIREMENTS_LIST_TEXTS.ITEM5}
        </StyledListItem>
      </StyledList>
    </StyledContainer>
  );
};
